import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import api from "@/lib/api";

const STOPS = [
  { path: "/portal", eyebrow: "Home Base", title: "Your Dashboard", copy: "Every project, artwork request, and notification lives here — one screen to run your beverage brand." },
  { path: "/portal/reorder", eyebrow: "Live Status", title: "Production Tracker", copy: "Watch your order move through 12 production stages in real time. We push updates the moment they happen." },
  { path: "/portal/artwork", eyebrow: "Version Control", title: "Artwork Approval", copy: "Approve or request revisions on every artwork version. Approved files auto-drop into your Brand Vault." },
  { path: "/portal/vault", eyebrow: "Your Kit", title: "Brand Vault", copy: "Every logo, label, palette, font, website and domain — downloadable individually or as a single ZIP." },
  { path: "/portal/support", eyebrow: "One Inbox", title: "Support Center", copy: "Tickets, threaded conversations, file attachments. No more scattered email chains." },
  { path: "/portal", eyebrow: "Stay Informed", title: "Notifications", copy: "The bell in the header lights up whenever something happens. Click it to catch up in seconds." },
];

const OnboardingTour = () => {
  const [step, setStep] = useState(-1); // -1 = welcome modal, 0..N = tour stops, -2 = closed
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only trigger on /portal for logged-in clients
    if (!location.pathname.startsWith("/portal")) return;
    const done = localStorage.getItem("flowtalis_onboarding");
    if (done === "completed" || done === "skipped") return;
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const mark = async (status) => {
    localStorage.setItem("flowtalis_onboarding", status);
    setVisible(false);
    try { await api.post("/portal/onboarding", { status }); } catch { /* silent */ }
  };

  if (!visible) return null;

  // Welcome modal
  if (step === -1) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="onboarding-welcome">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
          <div className="bg-[var(--ft-black)] p-6 text-center">
            <Sparkles size={32} className="mx-auto mb-3 text-[var(--ft-gold)]" />
            <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[var(--ft-gold)] font-semibold">Welcome</div>
            <div className="text-white text-2xl font-bold tracking-tight mt-2">FLOWTALIS™</div>
          </div>
          <div className="p-6 text-center">
            <h2 className="text-xl font-bold tracking-tight">Welcome to your Client Success Portal.</h2>
            <p className="text-sm text-[var(--ft-muted)] mt-3 leading-relaxed">
              This is where you&apos;ll manage every stage of your beverage brand — artwork approvals,
              production tracking, brand assets, invoices and support — all in one place.
            </p>
            <p className="text-sm text-[var(--ft-charcoal)] mt-4">
              Take a quick 60-second tour to see what&apos;s where.
            </p>
          </div>
          <div className="p-4 border-t border-[var(--ft-border)] flex gap-2 justify-center">
            <button onClick={() => mark("skipped")} className="ft-btn-ghost" data-testid="onboarding-skip">
              Skip for Now
            </button>
            <button onClick={() => setStep(0)} className="ft-btn-primary" data-testid="onboarding-start">
              Start Tour <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tour step
  const s = STOPS[step];
  if (!s) return null;
  const isLast = step === STOPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 pointer-events-none" data-testid={`onboarding-step-${step}`}>
      <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={() => mark("skipped")} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full pointer-events-auto overflow-hidden">
        <div className="px-6 pt-5 pb-3 border-b border-[var(--ft-border)] flex items-center justify-between">
          <div className="text-xs text-[var(--ft-muted)] font-semibold">
            Step {step + 1} of {STOPS.length}
          </div>
          <button onClick={() => mark("skipped")} className="ft-btn-ghost" data-testid="onboarding-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-6">
          <div className="ft-eyebrow">{s.eyebrow}</div>
          <h3 className="text-lg font-bold tracking-tight mt-1">{s.title}</h3>
          <p className="text-sm text-[var(--ft-charcoal)] mt-3 leading-relaxed">{s.copy}</p>
          <div className="mt-4 flex gap-1">
            {STOPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[var(--ft-gold)]" : "bg-[var(--ft-border)]"}`} />
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--ft-border)] flex justify-between">
          <button onClick={() => mark("skipped")} className="ft-btn-ghost" data-testid="onboarding-skip-tour">
            Skip Tour
          </button>
          <button
            onClick={() => {
              if (isLast) {
                mark("completed");
                navigate("/portal");
              } else {
                const next = step + 1;
                navigate(STOPS[next].path);
                setStep(next);
              }
            }}
            className="ft-btn-primary"
            data-testid="onboarding-next"
          >
            {isLast ? <><CheckCircle2 size={14} /> Finish</> : <>Next <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
